/* eslint-disable import/order */
import App from '@/App.vue'
import vuetify from '@/plugins/vuetify'
import { loadFonts } from '@/plugins/webfontloader'
import router from '@/router'
import type { Router } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createApp, markRaw } from 'vue'
import i18n from '@/plugins/i18n'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import '@sass/app.scss'
import * as Sentry from '@sentry/vue'
import yupSetup from '@/plugins/yupSetup'

loadFonts()

declare module 'pinia' {
  export interface PiniaCustomProperties {
    // type the router added by the plugin above (#adding-new-external-properties)
    router: Router
  }
}

// Create vue app
const app = createApp(App)
const pinia = createPinia()

// Globally load router into Pinia
pinia.use(({ store }) => {
  store.router = markRaw(router)
})

// Use plugins
app.use(vuetify)
app.use(createPinia())
app.use(router)
app.use(VueQueryPlugin, {
  enableDevtoolsV6Plugin: true,
})
app.use(i18n)
app.use(VueDOMPurifyHTML, {
  hooks: {
    afterSanitizeAttributes: (currentNode: HTMLElement) => {
      // set all elements owning target to target=_blank
      if ('target' in currentNode) {
        currentNode.setAttribute('target', '_blank')
        currentNode.setAttribute('rel', 'noopener')
      }
    },
  },
})
// Pull in the App Config passed from PHP

const appStore = useAppStore()
const configJson = document.getElementById('app')?.getAttribute('data-config')
const configData = (configJson) ? JSON.parse(configJson) : null

const settingsJson = document.getElementById('app')?.getAttribute('data-settings')
const settingsData = (settingsJson) ? JSON.parse(settingsJson) : null

appStore.config = configData
appStore.settings = settingsData
appStore.$i18n = i18n

const sentryIntegrations = [
  Sentry.browserTracingIntegration({ router }),
  Sentry.captureConsoleIntegration(),
]

if (configData.SENTRY_USER_FEEDBACK) {
  sentryIntegrations.push(Sentry.feedbackIntegration({ colorScheme: 'system', autoInject: false }))
}

Sentry.init({
  app,
  debug: false,
  dsn: configData.SENTRY_DSN_PUBLIC,
  release: configData.SENTRY_RELEASE,
  environment: configData.APP_ENV,
  integrations: sentryIntegrations,
  beforeSend(event, hint) {
    if (!event.exception || hint?.originalException === undefined) {
      return null
    }

    if (configData.SENTRY_USER_FEEDBACK && event.event_id) {
      Sentry.showReportDialog({ eventId: event.event_id })
    }

    return event
  },
})

yupSetup()

// Mount vue app
app.mount('#app')
